import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { AlertTriangle } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { formatCurrency } from '@/lib/utils';

interface WriteOffModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoice: {
    id: string;
    invoice_number: string;
    total_amount: number;
    paid_amount: number;
  };
}

export const WriteOffModal: React.FC<WriteOffModalProps> = ({
  open,
  onOpenChange,
  invoice
}) => {
  const queryClient = useQueryClient();
  const remainingBalance = (invoice.total_amount || 0) - (invoice.paid_amount || 0);
  
  const [amount, setAmount] = useState(remainingBalance.toString());
  const [reason, setReason] = useState('');

  const writeOffMutation = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const writeOffAmount = parseFloat(amount);
      if (isNaN(writeOffAmount) || writeOffAmount <= 0) {
        throw new Error('Invalid write-off amount');
      }
      if (writeOffAmount > remainingBalance) {
        throw new Error('Write-off amount cannot exceed remaining balance');
      }
      if (!reason.trim()) {
        throw new Error('Reason is required');
      }

      // Create write-off record
      const { error: writeOffError } = await supabase
        .from('write_offs')
        .insert({
          invoice_id: invoice.id,
          amount: writeOffAmount,
          reason: reason.trim(),
          written_off_by: user.id,
          written_off_at: new Date().toISOString()
        });

      if (writeOffError) throw writeOffError;

      // Update invoice
      const newPaidAmount = (invoice.paid_amount || 0) + writeOffAmount;
      const { error: updateError } = await supabase
        .from('invoices')
        .update({
          paid_amount: newPaidAmount,
          status: newPaidAmount >= invoice.total_amount ? 'paid' : 'partially_paid',
          paid_at: newPaidAmount >= invoice.total_amount ? new Date().toISOString() : null,
          updated_at: new Date().toISOString()
        })
        .eq('id', invoice.id);

      if (updateError) throw updateError;
    },
    onSuccess: () => {
      toast.success('Balance written off successfully');
      queryClient.invalidateQueries({ queryKey: ['invoice', invoice.id] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice-payments', invoice.id] });
      queryClient.invalidateQueries({ queryKey: ['write-offs', invoice.id] });
      onOpenChange(false);
      setReason('');
      setAmount(remainingBalance.toString());
    },
    onError: (error: Error) => {
      console.error('Error writing off balance:', error);
      toast.error(error.message || 'Failed to write off balance');
    }
  });

  React.useEffect(() => {
    if (open) {
      setAmount(remainingBalance.toString());
      setReason('');
    }
  }, [open, remainingBalance]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Write Off Balance
          </DialogTitle>
          <DialogDescription>
            Write off the remaining balance for invoice {invoice.invoice_number}. 
            This action will mark the specified amount as uncollectible.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Invoice Summary */}
          <div className="bg-muted p-3 rounded-md space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total Amount:</span>
              <span className="whitespace-nowrap font-mono">{formatCurrency(invoice.total_amount || 0)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Paid Amount:</span>
              <span className="whitespace-nowrap font-mono">{formatCurrency(invoice.paid_amount || 0)}</span>
            </div>
            <div className="flex justify-between font-medium border-t pt-1 mt-1">
              <span>Remaining Balance:</span>
              <span className="whitespace-nowrap font-mono">{formatCurrency(remainingBalance)}</span>
            </div>
          </div>

          {/* Write-off Amount */}
          <div className="space-y-2">
            <Label htmlFor="amount">Write-off Amount</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">R</span>
              <Input
                id="amount"
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="pl-7"
                max={remainingBalance}
                step="0.01"
              />
            </div>
          </div>

          {/* Reason */}
          <div className="space-y-2">
            <Label htmlFor="reason">Reason for Write-off *</Label>
            <Textarea
              id="reason"
              placeholder="e.g., Customer bankruptcy, Uncollectible after multiple attempts..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
            />
          </div>

          {/* Warning */}
          <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-3 rounded-md text-sm text-amber-800 dark:text-amber-200">
            <p className="font-medium">Important:</p>
            <ul className="list-disc list-inside mt-1 space-y-1">
              <li>This action will be recorded in the audit trail</li>
              <li>The invoice will be marked as paid after write-off</li>
              <li>Write-offs cannot be reversed</li>
            </ul>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => writeOffMutation.mutate()}
            disabled={writeOffMutation.isPending || !reason.trim()}
            variant="destructive"
          >
            {writeOffMutation.isPending ? 'Processing...' : 'Write Off Balance'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
